"use client"
import * as React from "react"

import {
    ColumnDef,
    flexRender,
    ColumnFiltersState,
    getCoreRowModel,
    getPaginationRowModel,
    getFilteredRowModel,
    useReactTable,
    SortingState,
    getSortedRowModel,
    VisibilityState,
} from "@tanstack/react-table"
import { Button } from "@/components/ui/button"
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table"
import SearchBar from "./SearchBar"
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { Assets } from "./AssetColumns"
import { axiosPrivate } from "@/apiCalls/axios"
import { useLocation, useNavigate } from "react-router-dom"
import { exportAssetsToExcel } from "@/utils/ExcelDataExport"
import { fetchAllAssets } from "@/apiCalls/apiCalls"
import { errorNotification } from "@/utils/Notification"
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

interface DataTableProps<TData, TValue> {
    columns: ColumnDef<TData, TValue>[]
    data: TData[]
    filterNameColumn: string

}

export function AssetDataTables<TData, TValue>({
    columns,
    data,
    filterNameColumn
}: DataTableProps<TData, TValue>) {
    const [sorting, setSorting] = React.useState<SortingState>([])
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
    const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({
        created_at: false,
        updated_at: false,
        customer_name:false,
        technician_name:false,
        product_name:false,
        client_name:false,
        loan_id:false
    })
    const [rowSelection, setRowSelection] = React.useState({})
    const [filteredData, setFilteredData] = React.useState<TData[]>(data)
    const navigate = useNavigate()
    const location = useLocation()

    const getAssets = () => {
        let isMounted = true;
        const controller = new AbortController()
        axiosPrivate.get('/api/v1/assets/allAssets', {
            signal: controller.signal
        }).then((response: any) => response.data.data)
            .then((data: Assets[]) => {
                if (isMounted) {
                    exportAssetsToExcel(data, 'Assets')
                }
            }).catch((err: any) => {
                const errors = err?.response?.data?.errors;
                if (errors && errors?.[0]?.message === "token as expired") {
                    let errror = errors?.[0]?.message
                    errorNotification(
                        "There was an issue",
                        `${errror}`,
                        'topRight'
                    );
                    navigate('/', { state: { from: location }, replace: true });
                } else if (errors && Array.isArray(errors) && errors.length > 0) {
                    errors.forEach((error: any) => {
                        const errorMessage = error.message || "An unknown error occurred";
                        errorNotification(
                            "There was an issue",
                            `${errorMessage}`,
                            'topRight'
                        );
                    });
                } else {
                    errorNotification(
                        "There was an issue",
                        "No server reponse",
                        'topRight'
                    );
                    navigate('/', { state: { from: location }, replace: true });
                }
            })
        return () => {
            isMounted = false
            controller.abort()
        };
    };






    const table = useReactTable({
        data: filteredData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        onColumnFiltersChange: setColumnFilters,
        getFilteredRowModel: getFilteredRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        onRowSelectionChange: setRowSelection,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            rowSelection,
        },
    })
    return (
        <>
            <div className="flex items-center ">
                <div className="flex items-center py-4">
                    <SearchBar setData={setFilteredData} originalData={data} />
                </div>

                <Button
                    variant="outline"
                    className="ml-auto flex items-center space-x-2"
                    onClick={getAssets}
                >
                    <ArrowDownTrayIcon className="h-5 w-5" />
                    <span>Export</span>
                </Button>

                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="ml-auto">
                            Columns
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        {table
                            .getAllColumns()
                            .filter(
                                (column) => column.getCanHide()
                            )
                            .map((column) => {
                                return (
                                    <DropdownMenuCheckboxItem
                                        key={column.id}
                                        className="capitalize"
                                        checked={column.getIsVisible()}
                                        onCheckedChange={(value) =>
                                            column.toggleVisibility(!!value)
                                        }
                                    >
                                        {column.id}
                                    </DropdownMenuCheckboxItem>
                                )
                            })}
                    </DropdownMenuContent>
                </DropdownMenu>

            </div>
            <div className="rounded-md border  border-black">
                <Table className="min-w-full divide-y divide-black p-3 ">
                    <TableHeader className="">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id} className="divide-x divide-black">
                                {headerGroup.headers.map((header) => (
                                    <TableHead
                                        key={header.id}
                                        className="px-6 py-1 border-b border-black  text-left text-sm font-medium text-black  tracking-wider"
                                    >
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody className=" divide-y divide-black ">
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    data-state={row.getIsSelected() && "selected"}
                                    className="divide-x divide-black"
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell
                                            key={cell.id}
                                            className="px-6 py-1 whitespace-nowrap border-b border-gray-700 text-black"
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={columns.length}
                                    className="px-6 py-4 whitespace-nowrap text-center border-b border-gray-700 text-gray-300"
                                >
                                    No results.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            <div className="flex items-center justify-end space-x-2 py-4">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                >
                    Previous
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                >
                    Next
                </Button>
            </div>

        </>
    )
}
